import { Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LoginPage } from '@/features/auth/components/LoginPage';
import { RegisterPage } from '@/features/auth/components/RegisterPage';
import { LeadsPage } from '@/features/leads/components';
import { LeadSearchPage } from '@/features/leads/pages/LeadSearchPage';
import { JobsPage } from '@/features/jobs';
import { JobDetailPage } from '@/features/jobs/pages/JobDetailPage';
import { InsuranceCompaniesPage, InsuranceCompanyDetailPage } from '@/features/insurance';
import { UsersPage } from '@/features/users';
import { SubcontractorsPage } from '@/features/subcontractors';

// Placeholder components (will be replaced with actual feature components)
function Dashboard() {
  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold">Dashboard</h2>
      <p className="mt-4 text-muted-foreground">Welcome to Atlas Restoration</p>
    </div>
  );
}

function MyAccountPage() {
  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold">My Account</h2>
      <p className="mt-4 text-muted-foreground">Account settings will be implemented here</p>
    </div>
  );
}

function AboutPage() {
  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold">About</h2>
      <p className="mt-4 text-muted-foreground">About Atlas Restoration will be implemented here</p>
    </div>
  );
}

/**
 * Protected route wrapper that requires authentication
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

/**
 * Route definitions for the application
 */
export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <LeadsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/leads',
    element: (
      <ProtectedRoute>
        <LeadsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/leads/:id',
    element: (
      <ProtectedRoute>
        <LeadsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/leads/search',
    element: (
      <ProtectedRoute>
        <LeadSearchPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs',
    element: (
      <ProtectedRoute>
        <JobsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs/:id',
    element: (
      <ProtectedRoute>
        <JobDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/payments',
    element: (
      <ProtectedRoute>
        <div>Payments (coming soon)</div>
      </ProtectedRoute>
    ),
  },
  {
    path: '/reports',
    element: (
      <ProtectedRoute>
        <div>Reports (coming soon)</div>
      </ProtectedRoute>
    ),
  },
  {
    path: '/users',
    element: (
      <ProtectedRoute>
        <UsersPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/subcontractors',
    element: (
      <ProtectedRoute>
        <SubcontractorsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <div>Settings (coming soon)</div>
      </ProtectedRoute>
    ),
  },
  {
    path: '/insurance',
    element: (
      <ProtectedRoute>
        <InsuranceCompaniesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/insurance/:id',
    element: (
      <ProtectedRoute>
        <InsuranceCompanyDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/account',
    element: (
      <ProtectedRoute>
        <MyAccountPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/about',
    element: (
      <ProtectedRoute>
        <AboutPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];
